import useEventsAggregateByTimeRange from '@/Components/Hooks/useEventsAggregateByTimeRange';
import useGetLatestUserEventsMultiple from '@/Components/Hooks/useGetLatestUserEventsMultiple';
import useGetUserEventsTrend from '@/Components/Hooks/useGetUserEventsTrend';
import useGetUserMedicalAccessories from '@/Components/Hooks/useGetUserMedicalAccessories';
import useGetUserMotionEventsByTimeRange from '@/Components/Hooks/useGetUserMotionEventsByTimeRange';
import useGetUserState from '@/Components/Hooks/useGetUserState';
import useNotification from '@/Components/Hooks/useNotification';
import useSchedule from '@/Components/Hooks/useSchedule';
import { unitTitleData, wellnessEventTypes } from '@/Components/Wellness/WellnessUtils';
import {
  ACCESSORIES_PROPERTIES,
  BIOMETRIC_DEVICE_TYPES,
  BIOMETRIC_SSE_EVENTS,
  BIOMETRIC_SSE_EVENT_PROPERTIES,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_EVENT_TYPES,
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  DEVICE_UNITS,
  ENGAGEMENT_BUNDLE_EVENT_TYPES,
  ENGAGEMENT_BUNDLE_SSE_EVENTS,
  ENGAGEMENT_BUNDLE_SSE_EVENT_PROPERTIES,
  ENGAGEMENT_CARD_EVENTS,
  EVENT_PROPERTIES,
  EVENT_TYPES,
  FIFTEEN_MINUTES_MS,
  RECENT_ACTIVITY_EVENT_TYPES,
  SCHEDULE_EVENT_TYPE,
  SENSOR_SSE_EVENTS,
  USER_MEASUREMENTS,
} from '@/constants/device';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  kilogramsToPounds,
  poundsToKilograms,
} from '@/utilities/helpers/commonHelper';
import { get, isEmpty, pick } from 'lodash';
import moment from 'moment-timezone';
import { useContext, useEffect, useMemo } from 'react';
import { DashboardSSEEventContext } from '../../DashboardContext';
import { CATEGORIES } from '../../utils';
import {
  RECENT_ACTIVITY_CARD_TYPES,
  RECENT_ACTIVITY_SMART_HOME_CARD_TYPES,
  mergeCallData,
  sortByLatestTimestamp,
} from './RecentActivityUtils';

const getSmartHomeEventKey = (event = {}) =>
  `${get(event, EVENT_PROPERTIES.DEVICE_UUID, '')}-${get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')}`;

const BIOMETRIC_SSE_EVENT_TO_TYPE = {
  [wellnessEventTypes.BLOOD_PRESSURE]: BIOMETRIC_DEVICE_TYPES.BLOOD_PRESSURE,
  [wellnessEventTypes.GLUCOSE]: BIOMETRIC_DEVICE_TYPES.GLUCOSE_METER,
  [wellnessEventTypes.PULSE_OXIMETER]: BIOMETRIC_DEVICE_TYPES.PULSE_OXIMETER,
  [wellnessEventTypes.THERMOMETER]: BIOMETRIC_DEVICE_TYPES.THERMOMETER,
  [wellnessEventTypes.WEIGHT_SCALE]: BIOMETRIC_DEVICE_TYPES.WEIGHT_SCALE,
};

const getBiometricDataPoint = (
  label,
  value,
  appendDegree = false,
  measurementSystem = USER_MEASUREMENTS.IMPERIAL,
  sourceUnit = null
) => {
  const displayLabel = get(unitTitleData, [measurementSystem, label], '');
  let displayValue = value;

  // Apply unit conversion for temperature and weight
  if ((label === 'temperature' || label === 'weight') && value !== null && value !== undefined) {
    if (label === 'temperature' && sourceUnit) {
      const isSourceMetric = sourceUnit === DEVICE_UNITS.TEMPERATURE.C;
      if (measurementSystem === USER_MEASUREMENTS.METRIC && !isSourceMetric) {
        displayValue = fahrenheitToCelsius(value);
      } else if (measurementSystem === USER_MEASUREMENTS.IMPERIAL && isSourceMetric) {
        displayValue = celsiusToFahrenheit(value);
      }
    } else if (label === 'weight' && sourceUnit) {
      const isSourceMetric = ['kg', 'kgs'].includes(sourceUnit);
      if (measurementSystem === USER_MEASUREMENTS.METRIC && !isSourceMetric) {
        displayValue = poundsToKilograms(value);
      } else if (measurementSystem === USER_MEASUREMENTS.IMPERIAL && isSourceMetric) {
        displayValue = kilogramsToPounds(value);
      }
    }
  }

  const numericDisplayValue = Number(displayValue);
  const formattedDisplayValue =
    label === 'weight' && Number.isFinite(numericDisplayValue)
      ? numericDisplayValue.toFixed(1)
      : displayValue;

  return {
    display_label: displayLabel,
    display_value:
      formattedDisplayValue === null ||
      formattedDisplayValue === undefined ||
      formattedDisplayValue === ''
        ? '-'
        : `${formattedDisplayValue}${appendDegree ? '°' : ''}`,
  };
};

const normalizeBiometricEvent = (event = {}, measurementSystem = USER_MEASUREMENTS.IMPERIAL) => {
  if (isEmpty(event)) return null;

  const eventType = get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  if (!BIOMETRIC_SSE_EVENTS.includes(eventType)) return null;

  const biometricType = BIOMETRIC_SSE_EVENT_TO_TYPE[eventType];
  if (!biometricType) return null;

  const timestamp =
    get(event, EVENT_PROPERTIES.TIMESTAMP, null) ||
    get(event, EVENT_PROPERTIES.RECV_TIME, null) ||
    get(event, EVENT_PROPERTIES.SEND_TIME, null);

  const props = BIOMETRIC_SSE_EVENT_PROPERTIES[eventType];
  let data = [];

  if (eventType === wellnessEventTypes.BLOOD_PRESSURE && props) {
    data = [
      getBiometricDataPoint('systolic', get(event, props.systolic, null), false, measurementSystem),
      getBiometricDataPoint(
        'diastolic',
        get(event, props.diastolic, null),
        false,
        measurementSystem
      ),
      getBiometricDataPoint('pulse', get(event, props.pulse_rate, null), false, measurementSystem),
    ];
  }

  if (eventType === wellnessEventTypes.GLUCOSE && props) {
    data = [
      getBiometricDataPoint(
        'glucose_concentration',
        get(event, props.glucose_concentration, null),
        false,
        measurementSystem
      ),
    ];
  }

  if (eventType === wellnessEventTypes.PULSE_OXIMETER && props) {
    data = [
      getBiometricDataPoint('spo2', get(event, props.spo2, null), false, measurementSystem),
      getBiometricDataPoint('pulse', get(event, props.pulse_rate, null), false, measurementSystem),
    ];
  }

  if (eventType === wellnessEventTypes.THERMOMETER && props) {
    const temperatureUnit = get(event, props.temperature_unit, '');
    data = [
      getBiometricDataPoint(
        'temperature',
        get(event, props.temperature, null),
        temperatureUnit === DEVICE_UNITS.TEMPERATURE.F,
        measurementSystem,
        temperatureUnit
      ),
    ];
  }

  if (eventType === wellnessEventTypes.WEIGHT_SCALE && props) {
    const weightUnit = get(event, props.weight_unit, '');
    data = [
      getBiometricDataPoint(
        'weight',
        get(event, props.weight, null),
        false,
        measurementSystem,
        weightUnit
      ),
    ];
  }

  return {
    title: biometricType,
    [EVENT_PROPERTIES.TIMESTAMP]: timestamp,
    eventName: eventType,
    device_uuid: get(event, EVENT_PROPERTIES.DEVICE_UUID, ''),
    data,
  };
};

const normalizeEngagementBundleEvent = (event = {}) => {
  if (isEmpty(event)) return null;

  const eventType = get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  if (!ENGAGEMENT_BUNDLE_SSE_EVENTS.includes(eventType)) return null;

  const timestamp =
    get(event, EVENT_PROPERTIES.TIMESTAMP, null) ||
    get(event, EVENT_PROPERTIES.RECV_TIME, null) ||
    get(event, EVENT_PROPERTIES.SEND_TIME, null);

  const props = ENGAGEMENT_BUNDLE_SSE_EVENT_PROPERTIES[eventType] || {};
  const eventData = {
    [EVENT_PROPERTIES.TIMESTAMP]: timestamp,
    [EVENT_PROPERTIES.UTC_TIMESTAMP]: timestamp,
  };

  if (eventType === ENGAGEMENT_BUNDLE_EVENT_TYPES.CHANNEL_LAUNCHED) {
    eventData[EVENT_PROPERTIES.CHANNEL_NAME] =
      get(event, props.channel_name, '') || get(event, props.channel_id, '');
  }

  if (eventType === ENGAGEMENT_BUNDLE_EVENT_TYPES.APP_LAUNCH) {
    eventData[EVENT_PROPERTIES.APP_NAME] = get(event, props.app_name, '');
    eventData.app_type = get(event, props.app_type, '');
    eventData.app_details = get(event, props.app_details, '');
  }

  return {
    [eventType]: eventData,
    [EVENT_PROPERTIES.TIMESTAMP]: timestamp,
  };
};

const toComparableTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const numericTimestamp = Number(value);
  if (!Number.isNaN(numericTimestamp)) {
    return numericTimestamp;
  }

  const parsedTimestamp = Date.parse(value);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

const isIncomingEventNewer = (incomingEvent = {}, currentEvent = {}) => {
  const incomingTimestamp = toComparableTimestamp(
    get(incomingEvent, EVENT_PROPERTIES.TIMESTAMP, null) ||
      get(incomingEvent, EVENT_PROPERTIES.RECV_TIME, null) ||
      get(incomingEvent, EVENT_PROPERTIES.SEND_TIME, null)
  );
  const currentTimestamp = toComparableTimestamp(
    get(currentEvent, EVENT_PROPERTIES.TIMESTAMP, null) ||
      get(currentEvent, EVENT_PROPERTIES.RECV_TIME, null) ||
      get(currentEvent, EVENT_PROPERTIES.SEND_TIME, null)
  );

  if (incomingTimestamp === null && currentTimestamp === null) {
    return true;
  }

  if (incomingTimestamp === null) {
    return false;
  }

  if (currentTimestamp === null) {
    return true;
  }

  return incomingTimestamp >= currentTimestamp;
};

const normalizeSmartHomeEvent = (event = {}, accessories = []) => {
  if (isEmpty(event)) return null;

  const liveEventType = get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  if (!SENSOR_SSE_EVENTS.includes(liveEventType)) {
    return null;
  }

  const liveDeviceUuid = get(event, EVENT_PROPERTIES.DEVICE_UUID, '');
  const accessory = (accessories || []).find(
    (item) => get(item, ACCESSORIES_PROPERTIES.DEVICE_UUID, '') === liveDeviceUuid
  );
  const location = get(accessory, ACCESSORIES_PROPERTIES.LOCATION, '');

  const normalizedLiveEvent = {
    ...event,
    [EVENT_PROPERTIES.TIMESTAMP]:
      get(event, EVENT_PROPERTIES.TIMESTAMP, null) ||
      get(event, EVENT_PROPERTIES.RECV_TIME, null) ||
      get(event, EVENT_PROPERTIES.SEND_TIME, null),
    ...(location ? { [EVENT_PROPERTIES.LOCATION]: location } : {}),
  };

  if (
    [
      EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN,
      EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_BLE_DOOR_WINDOW_SENSOR,
    ].includes(liveEventType)
  ) {
    normalizedLiveEvent.open = get(
      event,
      DEVICE_STATE_PROPERTY.door_window_sensor,
      get(
        event,
        DEVICE_STATE_PROPERTY.door_window_sensor_ble,
        get(event, DEVICE_EVENTS_PROPERTY.accessory_state_update_door_window_sensor_open, null)
      )
    );
  }

  if (
    [
      EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE,
      EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_BLE_OCCUPANCY_SENSOR,
    ].includes(liveEventType)
  ) {
    normalizedLiveEvent.active = get(
      event,
      DEVICE_STATE_PROPERTY.occupancy_sensor,
      get(event, DEVICE_STATE_PROPERTY.occupancy_sensor_ble, null)
    );
  }

  if (DEVICE_EVENT_TYPES[DEVICE_TYPES.TOILET_FLUSH_SENSOR].includes(liveEventType)) {
    normalizedLiveEvent.active = get(
      event,
      DEVICE_STATE_PROPERTY.toilet_flush_sensor,
      get(event, DEVICE_STATE_PROPERTY.toilet_flush_sensor_ble, null)
    );
  }

  if (liveEventType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON) {
    normalizedLiveEvent.on = get(
      event,
      DEVICE_STATE_PROPERTY.smart_plug,
      get(event, DEVICE_EVENTS_PROPERTY.accessory_state_update_smart_plug_on, null)
    );
  }

  return normalizedLiveEvent;
};

const buildMergedSmartHomeData = (smartHomeData = [], persistedSseEventsByKey = {}) => {
  const mergedEventsByKey = {};

  (Array.isArray(smartHomeData) ? smartHomeData : []).forEach((event) => {
    const key = getSmartHomeEventKey(event);
    if (!key || key === '-') return;

    const existingEvent = mergedEventsByKey[key];
    if (!existingEvent || isIncomingEventNewer(event, existingEvent)) {
      mergedEventsByKey[key] = event;
    }
  });

  Object.values(persistedSseEventsByKey || {}).forEach((event) => {
    const key = getSmartHomeEventKey(event);
    if (!key || key === '-') return;

    const existingEvent = mergedEventsByKey[key];
    if (!existingEvent || isIncomingEventNewer(event, existingEvent)) {
      mergedEventsByKey[key] = event;
    }
  });

  return Object.values(mergedEventsByKey).sort((a, b) => {
    const aTimestamp =
      toComparableTimestamp(get(a, EVENT_PROPERTIES.TIMESTAMP, null)) || Number.MIN_SAFE_INTEGER;
    const bTimestamp =
      toComparableTimestamp(get(b, EVENT_PROPERTIES.TIMESTAMP, null)) || Number.MIN_SAFE_INTEGER;
    return bTimestamp - aTimestamp;
  });
};

export const useFetchRecentActivityData = (props) => {
  const {
    accessories,
    useruuid,
    measurementSystem,
    supportedCategories = [],
    hubRecord,
    timezone,
  } = props;
  const timezoneName = useMemo(() => timezone?.split(' ')[0] || moment.tz.guess(), [timezone]);
  const { startDatetime, endDatetime } = useMemo(() => {
    if (!timezoneName) {
      return { startDatetime: null, endDatetime: null };
    }
    const nowInZone = moment.tz(timezoneName);
    const startOfRange = nowInZone
      .clone()
      .subtract(0, 'day')
      .startOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endOfRange = nowInZone.clone().endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    return { startDatetime: startOfRange, endDatetime: endOfRange };
  }, [timezoneName]);
  const aggregateFields = [{ name: 'active', values: ['true'] }];
  const deviceuuid = get(hubRecord, ACCESSORIES_PROPERTIES.DEVICE_UUID, '');
  const envEventTypes = DEVICE_EVENT_TYPES[DEVICE_TYPES.TOILET_FLUSH_SENSOR];
  const { data: aggregatedEvents } = useEventsAggregateByTimeRange({
    useruuid,
    deviceuuid,
    envEventTypes,
    startTime: startDatetime,
    endTime: endDatetime,
    aggregateFields,
  });
  const motionDevices = useMemo(
    () =>
      (accessories || []).filter((a) =>
        ['HC200', 'EM200'].includes(get(a, ACCESSORIES_PROPERTIES.MODEL_NUMBER, ''))
      ),
    [accessories]
  );

  const smartHomeDeviceUuids = useMemo(
    () =>
      (accessories || [])
        .filter((a) =>
          Object.values(RECENT_ACTIVITY_SMART_HOME_CARD_TYPES).includes(
            get(a, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '')
          )
        )
        .map((a) => get(a, ACCESSORIES_PROPERTIES.DEVICE_UUID, ''))
        .filter(Boolean),
    [accessories]
  );

  // Fetch the recent calls data
  const [notificationData, fetchingNotificationData, fetchNotificationData] = useNotification({
    ...props,
    callhistory: true,
    fetchDays: 7,
  });

  useEffect(() => {
    if (!useruuid) return;
    const id = setInterval(
      () => fetchNotificationData(undefined, { skipClear: true }),
      FIFTEEN_MINUTES_MS
    );
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useruuid]);

  // Fetch schedule events for the past 7 days (same range as notifications)
  const [fetchingScheduleList, scheduleList, fetchSchedule] = useSchedule();
  useEffect(() => {
    if (useruuid) {
      fetchSchedule(useruuid, new Date(), 7);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useruuid]);

  useEffect(() => {
    if (!useruuid) return;
    const id = setInterval(() => fetchSchedule(useruuid, new Date(), 7), FIFTEEN_MINUTES_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useruuid]);

  const { data: stateData, isFetching: fetchingStateData } = useGetUserState(
    { ...props },
    { refetchInterval: FIFTEEN_MINUTES_MS }
  );

  // Fetch motion events for the past 2 days (as HC200/EM200 are motion sensors)
  const { data: motionData = [], isFetching: fetchingMotionData } =
    useGetUserMotionEventsByTimeRange({
      ...props,
      fetchDays: 2,
      enabled: !isEmpty(motionDevices) && supportedCategories.includes(CATEGORIES.MOTION),
    });

  // Fetch medical accessories data
  const { data: medicalAccessoriesData, isFetching: fetchingMedicalAccessoriesData } =
    useGetUserMedicalAccessories({
      ...props,
      enabled: supportedCategories.includes(CATEGORIES.BIOMETRIC),
    });

  // Fetch smart home devices latest events data
  const { data: smartHomeData, isFetching: fetchingSmartHomeData } = useGetLatestUserEventsMultiple(
    {
      ...props,
      deviceuuids: smartHomeDeviceUuids,
      enabled: supportedCategories.includes(CATEGORIES.SMART_HOME),
    }
  );

  const { eventStream: recentActivityEventStream = [] } = useContext(DashboardSSEEventContext);

  const smartHomeEventStream = useMemo(
    () =>
      (recentActivityEventStream || []).filter((event) =>
        SENSOR_SSE_EVENTS.includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
      ),
    [recentActivityEventStream]
  );

  const biometricEventStream = useMemo(
    () =>
      (recentActivityEventStream || []).filter((event) =>
        BIOMETRIC_SSE_EVENTS.includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
      ),
    [recentActivityEventStream]
  );

  const engagementEventStream = useMemo(
    () =>
      (recentActivityEventStream || []).filter((event) =>
        ENGAGEMENT_BUNDLE_SSE_EVENTS.includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
      ),
    [recentActivityEventStream]
  );

  const persistedSseEventsByKey = useMemo(() => {
    return (smartHomeEventStream || []).reduce((acc, event) => {
      const normalizedLiveEvent = normalizeSmartHomeEvent(event, accessories);
      if (!normalizedLiveEvent) return acc;

      const eventKey = getSmartHomeEventKey(normalizedLiveEvent);
      if (!eventKey || eventKey === '-') return acc;

      const existingEvent = acc[eventKey];
      if (!existingEvent || isIncomingEventNewer(normalizedLiveEvent, existingEvent)) {
        acc[eventKey] = normalizedLiveEvent;
      }

      return acc;
    }, {});
  }, [smartHomeEventStream, accessories]);

  const mergedSmartHomeData = useMemo(
    () => buildMergedSmartHomeData(smartHomeData, persistedSseEventsByKey),
    [smartHomeData, persistedSseEventsByKey]
  );

  const persistedBiometricEventsByType = useMemo(() => {
    return (biometricEventStream || []).reduce((acc, event) => {
      const normalizedBiometricEvent = normalizeBiometricEvent(event, measurementSystem);
      if (!normalizedBiometricEvent) return acc;

      const biometricType = get(normalizedBiometricEvent, 'title', '');
      if (!biometricType) return acc;

      const existingEvent = acc[biometricType];
      if (!existingEvent || isIncomingEventNewer(normalizedBiometricEvent, existingEvent)) {
        acc[biometricType] = normalizedBiometricEvent;
      }

      return acc;
    }, {});
  }, [biometricEventStream, measurementSystem]);

  const persistedEngagementEventsByType = useMemo(() => {
    return (engagementEventStream || []).reduce((acc, event) => {
      const normalizedEngagementEvent = normalizeEngagementBundleEvent(event);
      if (!normalizedEngagementEvent) return acc;

      Object.entries(normalizedEngagementEvent).forEach(([key, value]) => {
        if (ENGAGEMENT_CARD_EVENTS.includes(key)) {
          const existingEvent = acc[key];
          if (!existingEvent || isIncomingEventNewer(value, existingEvent)) {
            acc[key] = value;
          }
        }
      });

      return acc;
    }, {});
  }, [engagementEventStream]);

  const mergedMedicalAccessoriesData = useMemo(() => {
    const baseMedicalAccessories = get(medicalAccessoriesData, 'medical_accessories', {});
    const mergedMedicalAccessories = { ...baseMedicalAccessories };

    Object.entries(persistedBiometricEventsByType || {}).forEach(([biometricType, sseEvent]) => {
      const existingEvent = get(mergedMedicalAccessories, biometricType, null);
      if (!existingEvent || isIncomingEventNewer(sseEvent, existingEvent)) {
        mergedMedicalAccessories[biometricType] = sseEvent;
      }
    });

    return {
      ...(medicalAccessoriesData || {}),
      medical_accessories: mergedMedicalAccessories,
    };
  }, [medicalAccessoriesData, persistedBiometricEventsByType]);

  // // Fetch weight scale trend data
  // const weightScaleRawData = get(
  //   mergedMedicalAccessoriesData,
  //   ['medical_accessories', BIOMETRIC_DEVICE_TYPES.WEIGHT_SCALE],
  //   null
  // );
  // const weightScaleDeviceUuid = get(weightScaleRawData, 'device_uuid', '');
  // const weightScaleEventName = get(weightScaleRawData, 'eventName', '');
  // const weightTrendBody =
  //   weightScaleDeviceUuid && weightScaleEventName
  //     ? [{ deviceUuid: weightScaleDeviceUuid, eventType: [weightScaleEventName] }]
  //     : null;

  // const { data: weightTrendData, isLoading: weightTrendLoading } = useGetUserEventsTrend({
  //   useruuid,
  //   trendBody: weightTrendBody,
  //   enabled: !!weightScaleDeviceUuid && !!weightScaleEventName && supportedCategories.includes(CATEGORIES.BIOMETRIC),
  // });

  // // Blood pressure trend
  // const bloodPressureRawData = get(
  //   mergedMedicalAccessoriesData,
  //   ['medical_accessories', BIOMETRIC_DEVICE_TYPES.BLOOD_PRESSURE],
  //   null
  // );
  // // const bloodPressureDeviceUuid = get(bloodPressureRawData, 'device_uuid', '');
  // // const bloodPressureEventName = get(bloodPressureRawData, 'eventName', '');
  // // const bloodPressureTrendBody =
  // //   bloodPressureDeviceUuid && bloodPressureEventName
  // //     ? [{ deviceUuid: bloodPressureDeviceUuid, eventType: [bloodPressureEventName] }]
  // //     : null;
  // // const { data: bloodPressureTrendData, isLoading: bloodPressureTrendLoading } =
  // //   useGetUserEventsTrend({
  // //     useruuid,
  // //     trendBody: bloodPressureTrendBody,
  // //     enabled: !!bloodPressureDeviceUuid && !!bloodPressureEventName && supportedCategories.includes(CATEGORIES.BIOMETRIC),
  // //   });

  // // Glucose meter trend
  // const glucoseRawData = get(
  //   mergedMedicalAccessoriesData,
  //   ['medical_accessories', BIOMETRIC_DEVICE_TYPES.GLUCOSE_METER],
  //   null
  // );
  // const glucoseDeviceUuid = get(glucoseRawData, 'device_uuid', '');
  // const glucoseEventName = get(glucoseRawData, 'eventName', '');
  // const glucoseTrendBody =
  //   glucoseDeviceUuid && glucoseEventName
  //     ? [{ deviceUuid: glucoseDeviceUuid, eventType: [glucoseEventName] }]
  //     : null;
  // const { data: glucoseTrendData, isLoading: glucoseTrendLoading } = useGetUserEventsTrend({
  //   useruuid,
  //   trendBody: glucoseTrendBody,
  //   enabled: !!glucoseDeviceUuid && !!glucoseEventName && supportedCategories.includes(CATEGORIES.BIOMETRIC),
  // });

  // // Pulse oximeter trend
  // const pulseOximeterRawData = get(
  //   mergedMedicalAccessoriesData,
  //   ['medical_accessories', BIOMETRIC_DEVICE_TYPES.PULSE_OXIMETER],
  //   null
  // );
  // const pulseOximeterDeviceUuid = get(pulseOximeterRawData, 'device_uuid', '');
  // const pulseOximeterEventName = get(pulseOximeterRawData, 'eventName', '');
  // const pulseOximeterTrendBody =
  //   pulseOximeterDeviceUuid && pulseOximeterEventName
  //     ? [{ deviceUuid: pulseOximeterDeviceUuid, eventType: [pulseOximeterEventName] }]
  //     : null;
  // const { data: pulseOximeterTrendData, isLoading: pulseOximeterTrendLoading } =
  //   useGetUserEventsTrend({
  //     useruuid,
  //     trendBody: pulseOximeterTrendBody,
  //     enabled: !!pulseOximeterDeviceUuid && !!pulseOximeterEventName && supportedCategories.includes(CATEGORIES.BIOMETRIC),
  //   });

  // // Thermometer trend
  // const thermometerRawData = get(
  //   mergedMedicalAccessoriesData,
  //   ['medical_accessories', BIOMETRIC_DEVICE_TYPES.THERMOMETER],
  //   null
  // );
  // const thermometerDeviceUuid = get(thermometerRawData, 'device_uuid', '');
  // const thermometerEventName = get(thermometerRawData, 'eventName', '');
  // const thermometerTrendBody =
  //   thermometerDeviceUuid && thermometerEventName
  //     ? [{ deviceUuid: thermometerDeviceUuid, eventType: [thermometerEventName] }]
  //     : null;
  // const { data: thermometerTrendData, isLoading: thermometerTrendLoading } = useGetUserEventsTrend({
  //   useruuid,
  //   trendBody: thermometerTrendBody,
  //   enabled: !!thermometerDeviceUuid && !!thermometerEventName && supportedCategories.includes(CATEGORIES.BIOMETRIC),
  // });

  // Motion trend (HC200 / EM200) — multiple devices, aggregated in card
  const motionTrendBody = !isEmpty(motionDevices)
    ? motionDevices
        .map((device) => ({
          deviceUuid: get(device, ACCESSORIES_PROPERTIES.DEVICE_UUID, ''),
          eventType: [RECENT_ACTIVITY_EVENT_TYPES.MOTION_DETECTED],
        }))
        .filter((entry) => entry.deviceUuid)
    : null;

  const { data: motionTrendData, isLoading: motionTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: motionTrendBody,
    enabled: !isEmpty(motionTrendBody) && supportedCategories.includes(CATEGORIES.MOTION),
    resolveRawResponse: true,
  });

  const callsTrendBody = [{ eventType: [RECENT_ACTIVITY_EVENT_TYPES.COMPLETED_CALLS] }];
  const { data: callsTrendData, isLoading: callsTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: callsTrendBody,
    resolveRawResponse: true,
  });

  // Smart home trend helpers
  const getLatestSmartHomeEventForTypes = (eventTypes) => {
    const filtered = (smartHomeData || []).filter((e) =>
      eventTypes.includes(get(e, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    return sortByLatestTimestamp(filtered, EVENT_PROPERTIES.TIMESTAMP)[0] || null;
  };

  // Door/Window sensor trend
  const latestDoorWindow = getLatestSmartHomeEventForTypes(
    DEVICE_EVENT_TYPES[DEVICE_TYPES.DOOR_WINDOW_SENSOR]
  );
  const doorWindowDeviceUuid = get(latestDoorWindow, EVENT_PROPERTIES.DEVICE_UUID, '');
  const doorWindowEventType = get(latestDoorWindow, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  const doorWindowTrendBody =
    doorWindowDeviceUuid && doorWindowEventType
      ? [{ deviceUuid: doorWindowDeviceUuid, eventType: [doorWindowEventType] }]
      : null;
  const { data: doorWindowTrendData, isLoading: doorWindowTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: doorWindowTrendBody,
    enabled:
      !!doorWindowDeviceUuid &&
      !!doorWindowEventType &&
      supportedCategories.includes(CATEGORIES.SMART_HOME),
  });

  // Environmental sensor trend averages all temperature-capable devices
  const environmentalTrendBody = useMemo(() => {
    const trendEventTypes = [
      EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT,
      EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR,
    ];

    const trendRequests = (accessories || []).flatMap((device) => {
      const deviceUuid = get(device, ACCESSORIES_PROPERTIES.DEVICE_UUID, '');
      const deviceType = get(device, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '');

      if (!deviceUuid) return [];
      if (
        deviceType !== DEVICE_TYPES.ENVIRONMENTAL_SENSOR &&
        deviceType !== DEVICE_TYPES.TEMPERATURE_SENSOR
      ) {
        return [];
      }

      const eventTypesForDevice = trendEventTypes.filter((eventType) =>
        (DEVICE_EVENT_TYPES[deviceType] || []).includes(eventType)
      );

      return eventTypesForDevice.map((eventType) => ({
        deviceUuid,
        eventType: [eventType],
      }));
    });

    return trendRequests;
  }, [accessories]);

  const { data: environmentalTrendDataRaw, isLoading: environmentalTrendLoading } =
    useGetUserEventsTrend({
      useruuid,
      trendBody: environmentalTrendBody,
      enabled:
        environmentalTrendBody.length > 0 && supportedCategories.includes(CATEGORIES.SMART_HOME),
      resolveRawResponse: true,
    });

  const environmentalTrendData = useMemo(() => {
    const trendResults = get(environmentalTrendDataRaw, 'trendResults', {});
    if (isEmpty(trendResults)) return null;

    const allTemperatureTrends = Object.values(trendResults).flatMap((deviceTrends) =>
      Object.values(deviceTrends || {}).flatMap((eventTrend) => {
        const nestedTemperatureTrend = get(eventTrend, 'temperature', null);
        if (nestedTemperatureTrend) return [nestedTemperatureTrend];

        const hasFlatTemperatureMetrics =
          get(eventTrend, 'baselineAvg', null) !== null ||
          get(eventTrend, 'trendPercent', null) !== null ||
          get(eventTrend, 'yesterdayCount', null) !== null;
        return hasFlatTemperatureMetrics ? [eventTrend] : [];
      })
    );

    if (allTemperatureTrends.length === 0) return null;

    const totals = allTemperatureTrends.reduce(
      (acc, trend) => ({
        baselineAvg: acc.baselineAvg + (Number(get(trend, 'baselineAvg', 0)) || 0),
        trendPercent: acc.trendPercent + (Number(get(trend, 'trendPercent', 0)) || 0),
        yesterdayCount: acc.yesterdayCount + (Number(get(trend, 'yesterdayCount', 0)) || 0),
      }),
      { baselineAvg: 0, trendPercent: 0, yesterdayCount: 0 }
    );

    const count = allTemperatureTrends.length;
    return {
      baselineAvg: totals.baselineAvg / count,
      trendPercent: Math.round(totals.trendPercent / count),
      yesterdayCount: totals.yesterdayCount / count,
      unit: get(allTemperatureTrends, [0, 'unit'], DEVICE_UNITS.TEMPERATURE.F),
    };
  }, [environmentalTrendDataRaw]);

  // Occupancy sensor trend
  const latestOccupancy = getLatestSmartHomeEventForTypes(
    DEVICE_EVENT_TYPES[DEVICE_TYPES.OCCUPANCY_SENSOR]
  );
  const occupancyDeviceUuid = get(latestOccupancy, EVENT_PROPERTIES.DEVICE_UUID, '');
  const occupancyEventType = get(latestOccupancy, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  const occupancyTrendBody =
    occupancyDeviceUuid && occupancyEventType
      ? [{ deviceUuid: occupancyDeviceUuid, eventType: [occupancyEventType] }]
      : null;
  const { data: occupancyTrendData, isLoading: occupancyTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: occupancyTrendBody,
    enabled:
      !!occupancyDeviceUuid &&
      !!occupancyEventType &&
      supportedCategories.includes(CATEGORIES.SMART_HOME),
  });

  // Smart plug trend
  const latestSmartPlug = getLatestSmartHomeEventForTypes(
    DEVICE_EVENT_TYPES[DEVICE_TYPES.SMART_PLUG]
  );
  const smartPlugDeviceUuid = get(latestSmartPlug, EVENT_PROPERTIES.DEVICE_UUID, '');
  const smartPlugEventType = get(latestSmartPlug, EVENT_PROPERTIES.MESSAGE_TYPE, '');
  const smartPlugTrendBody =
    smartPlugDeviceUuid && smartPlugEventType
      ? [{ deviceUuid: smartPlugDeviceUuid, eventType: [smartPlugEventType] }]
      : null;
  const { data: smartPlugTrendData, isLoading: smartPlugTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: smartPlugTrendBody,
    enabled:
      !!smartPlugDeviceUuid &&
      !!smartPlugEventType &&
      supportedCategories.includes(CATEGORIES.SMART_HOME),
  });

  // Toilet flush sensor trend
  const latestToiletFlush = getLatestSmartHomeEventForTypes(
    DEVICE_EVENT_TYPES[DEVICE_TYPES.TOILET_FLUSH_SENSOR]
  );
  const toiletFlushDeviceUuid = get(latestToiletFlush, EVENT_PROPERTIES.DEVICE_UUID, '');
  const toiletFlushEventType =
    EVENT_TYPES.FLUSH_SENSOR.ACCESSORY_STATE_UPDATE_TOILET_FLUSH_SENSOR_ACTIVE;
  const toiletFlushTrendBody =
    toiletFlushDeviceUuid && toiletFlushEventType
      ? [{ deviceUuid: toiletFlushDeviceUuid, eventType: [toiletFlushEventType] }]
      : null;
  const { data: toiletFlushTrendData, isLoading: toiletFlushTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: toiletFlushTrendBody,
    enabled:
      !!toiletFlushDeviceUuid &&
      !!toiletFlushEventType &&
      supportedCategories.includes(CATEGORIES.SMART_HOME),
    resolveRawResponse: true,
  });

  // Engagement trend — no deviceUuid, static event type
  const engagementTrendBody = [{ eventType: [RECENT_ACTIVITY_EVENT_TYPES.ENGAGEMENT_BUNDLE] }];
  const { data: engagementTrendData, isLoading: engagementTrendLoading } = useGetUserEventsTrend({
    useruuid,
    trendBody: engagementTrendBody,
    resolveRawResponse: true,
  });

  const recentActivityData = useMemo(() => {
    const bloodPressureData = get(
      mergedMedicalAccessoriesData,
      ['medical_accessories', BIOMETRIC_DEVICE_TYPES.BLOOD_PRESSURE],
      []
    );
    const glucoseMeterData = get(
      mergedMedicalAccessoriesData,
      ['medical_accessories', BIOMETRIC_DEVICE_TYPES.GLUCOSE_METER],
      []
    );
    const pulseOximeterData = get(
      mergedMedicalAccessoriesData,
      ['medical_accessories', BIOMETRIC_DEVICE_TYPES.PULSE_OXIMETER],
      []
    );
    const thermometerData = get(
      mergedMedicalAccessoriesData,
      ['medical_accessories', BIOMETRIC_DEVICE_TYPES.THERMOMETER],
      []
    );
    const weightScaleData = get(
      mergedMedicalAccessoriesData,
      ['medical_accessories', BIOMETRIC_DEVICE_TYPES.WEIGHT_SCALE],
      []
    );
    const engagementData = Object.entries(persistedEngagementEventsByType || {}).reduce(
      (acc, [eventType, sseEvent]) => {
        if (!ENGAGEMENT_CARD_EVENTS.includes(eventType)) return acc;

        const existingEvent = get(acc, eventType, null);
        if (!existingEvent || isIncomingEventNewer(sseEvent, existingEvent)) {
          acc[eventType] = sseEvent;
        }
        return acc;
      },
      { ...pick(stateData || {}, ENGAGEMENT_CARD_EVENTS) }
    );
    const doorWindowSensorData = (mergedSmartHomeData || []).filter((event) =>
      [
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN,
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_BLE_DOOR_WINDOW_SENSOR,
      ].includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    const environmentSensorData = (mergedSmartHomeData || []).filter((event) =>
      [
        ...DEVICE_EVENT_TYPES[DEVICE_TYPES.ENVIRONMENTAL_SENSOR],
        ...DEVICE_EVENT_TYPES[DEVICE_TYPES.TEMPERATURE_SENSOR],
      ].includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    const occupancySensorData = (mergedSmartHomeData || []).filter((event) =>
      DEVICE_EVENT_TYPES[DEVICE_TYPES.OCCUPANCY_SENSOR].includes(
        get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')
      )
    );
    const smartPlugData = (mergedSmartHomeData || []).filter((event) =>
      DEVICE_EVENT_TYPES[DEVICE_TYPES.SMART_PLUG].includes(
        get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')
      )
    );
    const toiletFlushSensorData = (mergedSmartHomeData || []).filter((event) =>
      DEVICE_EVENT_TYPES[DEVICE_TYPES.TOILET_FLUSH_SENSOR].includes(
        get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')
      )
    );
    const flushData =
      aggregatedEvents?.accessory_state_update_toilet_flush_sensor_active?.[0]?.active?.true || [];

    const filteredScheduleEvents = (scheduleList || []).filter(
      (e) => get(e, EVENT_PROPERTIES.EVENT_TYPE, '') === SCHEDULE_EVENT_TYPE.SCHEDULE
    );

    return {
      [RECENT_ACTIVITY_CARD_TYPES.RECENT_CALLS]: {
        isLoading: fetchingNotificationData || fetchingScheduleList,
        data: (() => {
          const merged = mergeCallData(notificationData, filteredScheduleEvents);
          return isEmpty(merged) ? null : merged;
        })(),
        trendData: callsTrendData || null,
        trendIsLoading: callsTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.MOTION]: {
        isLoading: fetchingMotionData,
        data: isEmpty(motionData) ? null : motionData,
        trendData: motionTrendData || null,
        trendIsLoading: motionTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.BLOOD_PRESSURE]: {
        isLoading: fetchingMedicalAccessoriesData,
        data: isEmpty(bloodPressureData) ? null : bloodPressureData,
        // trendData: bloodPressureTrendData || null,
        // trendIsLoading: bloodPressureTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.GLUCOSE_METER]: {
        isLoading: fetchingMedicalAccessoriesData,
        data: isEmpty(glucoseMeterData) ? null : glucoseMeterData,
        // trendData: glucoseTrendData || null,
        // trendIsLoading: glucoseTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.PULSE_OXIMETER]: {
        isLoading: fetchingMedicalAccessoriesData,
        data: isEmpty(pulseOximeterData) ? null : pulseOximeterData,
        // trendData: pulseOximeterTrendData || null,
        // trendIsLoading: pulseOximeterTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.THERMOMETER]: {
        isLoading: fetchingMedicalAccessoriesData,
        data: isEmpty(thermometerData) ? null : thermometerData,
        // trendData: thermometerTrendData || null,
        // trendIsLoading: thermometerTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.WEIGHT_SCALE]: {
        isLoading: fetchingMedicalAccessoriesData,
        data: isEmpty(weightScaleData) ? null : weightScaleData,
        // trendData: weightTrendData || null,
        // trendIsLoading: weightTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.ENGAGEMENT]: {
        isLoading: fetchingStateData,
        data: isEmpty(engagementData) ? null : engagementData,
        trendData: engagementTrendData || null,
        trendIsLoading: engagementTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.DOOR_WINDOW_SENSOR]: {
        isLoading: fetchingSmartHomeData,
        data: isEmpty(doorWindowSensorData) ? null : doorWindowSensorData,
        trendData: doorWindowTrendData || null,
        trendIsLoading: doorWindowTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.ENVIRONMENTAL_SENSOR]: {
        isLoading: fetchingSmartHomeData,
        data: isEmpty(environmentSensorData) ? null : environmentSensorData,
        trendData: environmentalTrendData || null,
        trendIsLoading: environmentalTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.OCCUPANCY_SENSOR]: {
        isLoading: fetchingSmartHomeData,
        data: isEmpty(occupancySensorData) ? null : occupancySensorData,
        trendData: occupancyTrendData || null,
        trendIsLoading: occupancyTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.SMART_PLUG]: {
        isLoading: fetchingSmartHomeData,
        data: isEmpty(smartPlugData) ? null : smartPlugData,
        trendData: smartPlugTrendData || null,
        trendIsLoading: smartPlugTrendLoading,
      },
      [RECENT_ACTIVITY_CARD_TYPES.TOILET_FLUSH_SENSOR]: {
        isLoading: fetchingSmartHomeData,
        data: isEmpty(toiletFlushSensorData) ? null : toiletFlushSensorData,
        aggregatedData: isEmpty(flushData) ? null : flushData,
        trendData: toiletFlushTrendData || null,
        trendIsLoading: toiletFlushTrendLoading,
      },
    };
  }, [
    notificationData,
    fetchingNotificationData,
    scheduleList,
    fetchingScheduleList,
    callsTrendData,
    callsTrendLoading,
    motionData,
    fetchingMotionData,
    motionTrendData,
    motionTrendLoading,
    fetchingStateData,
    mergedMedicalAccessoriesData,
    fetchingMedicalAccessoriesData,
    mergedSmartHomeData,
    stateData,
    fetchingSmartHomeData,
    persistedEngagementEventsByType,
    // weightTrendData,
    // weightTrendLoading,
    // bloodPressureTrendData,
    // bloodPressureTrendLoading,
    // glucoseTrendData,
    // glucoseTrendLoading,
    // pulseOximeterTrendData,
    // pulseOximeterTrendLoading,
    // thermometerTrendData,
    // thermometerTrendLoading,
    doorWindowTrendData,
    doorWindowTrendLoading,
    environmentalTrendData,
    environmentalTrendLoading,
    occupancyTrendData,
    occupancyTrendLoading,
    smartPlugTrendData,
    smartPlugTrendLoading,
    toiletFlushTrendData,
    toiletFlushTrendLoading,
    engagementTrendData,
    engagementTrendLoading,
    aggregatedEvents,
  ]);

  return { recentActivityData };
};
